import { StandardGroupingGrid, StandardGroupingGridPresentationType } from '@apply/components';
import { Column, MasterDetail, Summary, TotalItem } from 'devextreme-react/data-grid';
import { formatDecimalAsTwoDigitPercentage } from './functions/formatters/formatDecimalAsTwoDigitPercentage.tsx';
import { formatBoolAsYN } from '../../../../../common/functions/formatters/formatBoolAsYN.tsx';
import {
    BridgeAppraisalStatusGroupMapper,
    BridgeAppraisalStatusGroupSection,
} from './functions/mappers/bridgeAppraisalStatusGroupMapper.ts';
import { appraisedCellRender } from './functions/renderers/appraisedCellRender.tsx';
import { IBridgeApplicationGridRecord } from '../../../mockData/mockApplicationsGridData.ts';
import { BridgeAppraisalStatus } from '../../../../../common/constants/BridgeAppraisalStatus.tsx';
import { actionCellRender } from './functions/renderers/actionCellRender.tsx';
import { pocRender } from '../../../common/functions/renderers/pocRender.tsx';
import { creditScoreRateRender } from './functions/renderers/creditScoreRateRender.tsx';
import { experienceRender } from '../../../../../common/functions/renderers/experienceRender.tsx';
import { applicationCityRender } from '../../../common/functions/renderers/applicationCityRender.tsx';
import { applicationStateRender } from '../../../common/functions/renderers/applicationStateRender.tsx';
import { renderPortfolioRecords } from './functions/renderers/renderPortfolioRecords.tsx';

export interface ApplicationsGridProps {
    data: IBridgeApplicationGridRecord[] | undefined;
    selectedGroup: BridgeAppraisalStatusGroupSection | 'All';
}

export const ApplicationsGrid = ({ data, selectedGroup }: ApplicationsGridProps) => {
    const filteredData =
        selectedGroup === 'All'
            ? data
            : data?.filter((app) => {
                  const section = BridgeAppraisalStatusGroupMapper.getGroupSection(
                      app.appraisalStatusTypeId ?? BridgeAppraisalStatus.NothingReceived
                  );
                  return section === selectedGroup;
              });

    return (
        <>
            <StandardGroupingGrid
                dataSource={filteredData}
                keyExpr='applicationId'
                presentationType={StandardGroupingGridPresentationType.Enhanced}
                noDataText='No Applications Found'
                pagerVisible={false}
                groupPanelVisible={false}
                rowAlternationEnabled={true}
                allowCollapsing={false}
                showColumnHeaders={true}
                showBorders={true}
                remoteOperations={false}
                autoExpandAll={true}
                allowColumnReordering={false}
                masterDetailTrigger='isPortfolio'
                defaultPageSize={0}
            >
                <Column
                    dataField='appraisalStatusTypeId'
                    groupIndex={0}
                    calculateGroupValue={(rowData) => {
                        return BridgeAppraisalStatusGroupMapper.getGroupSection(rowData.appraisalStatusTypeId);
                    }}
                    sortOrder='asc'
                    groupCellRender={(cellInfo) => {
                        const label = BridgeAppraisalStatusGroupMapper.getGroupLabelsWithDescription(cellInfo.value, true);
                        return <i>{label}</i>;
                    }}
                    fixed={true}
                />

                <Column
                    dataField='loan'
                    caption='Loan'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='borrower'
                    caption='Borrower'
                    width={200}
                    alignment='left'
                />
                <Column
                    dataField='city'
                    caption='City'
                    width={120}
                    alignment='left'
                    cellRender={(e) => applicationCityRender(e)}
                />
                <Column
                    dataField='state'
                    caption='State'
                    width={50}
                    alignment='left'
                    cellRender={(e) => applicationStateRender(e)}
                />
                <Column
                    dataField='bfiRefi'
                    caption='BFI Refi'
                    width={70}
                    alignment='left'
                    dataType='string'
                    customizeText={(e) => formatBoolAsYN(e.value)}
                />
                <Column
                    dataField='units'
                    caption='Units'
                    width={50}
                    alignment='left'
                />
                <Column
                    dataField='estimatedSize'
                    caption='Est. Size'
                    width={100}
                    alignment='left'
                    format={'currency'}
                />
                <Column
                    dataField='estimatedRate'
                    caption='Est. Rate'
                    width={80}
                    alignment='left'
                    customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                />
                <Column
                    dataField='term'
                    caption='Term'
                    width={80}
                    alignment='left'
                />
                <Column
                    dataField='ltv'
                    caption='LTV'
                    width={60}
                    alignment='left'
                    customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                    cssClass='emphasizedGroup-start'
                />
                <Column
                    caption='Appraised'
                    width={100}
                    alignment='left'
                    cellRender={(cellInfo) =>
                        appraisedCellRender({
                            status: cellInfo.data.appraisalStatusTypeId,
                            appraisalAmount: cellInfo.data.appraisalAmount,
                            appraisalReceivedDate: cellInfo.data.appraisalReceivedDate,
                        })
                    }
                    cssClass='emphasizedGroup-end'
                />
                <Column
                    dataField='creditScoreRate'
                    caption='Credit'
                    width={100}
                    alignment='left'
                    cellRender={(e) => creditScoreRateRender(e)}
                />
                <Column
                    dataField='experience'
                    caption='Experience'
                    width={100}
                    alignment='left'
                    cellRender={(e) => experienceRender(e)}
                />
                <Column
                    dataField='appliedDate'
                    caption='Apply'
                    width={100}
                    alignment='left'
                    dataType='date'
                    format='M/dd/yyyy'
                />
                <Column
                    dataField='broker'
                    caption='Broker'
                    width={100}
                    alignment='left'
                />
                <Column
                    dataField='exception'
                    caption='Exception'
                    width={200}
                    alignment='left'
                />
                <Column
                    dataField='primaryMotivation'
                    caption='Primary Motivation'
                    width={150}
                    alignment='left'
                />
                <Column
                    caption='POC'
                    alignment='left'
                    width={60}
                    cellRender={(e) => pocRender(e)}
                />
                <Column
                    alignment='center'
                    cellRender={actionCellRender}
                    width={40}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    type='buttons'
                    fixed={true}
                    fixedPosition={'right'}
                />
                <MasterDetail
                    enabled={true}
                    render={renderPortfolioRecords}
                />
                <Summary
                    calculateCustomSummary={(options) => {
                        if (options.name === 'bfiRefi') {
                            if (options.summaryProcess === 'start') {
                                options.totalValue = 0;
                            }

                            if (options.summaryProcess === 'calculate') {
                                if (options.value === true) {
                                    options.totalValue += 1;
                                }
                            }
                        }
                        if (options.name === 'avgEstimatedRate') {
                            if (options.summaryProcess === 'start') {
                                options.totalValue = { sum: 0, count: 0 };
                            }

                            if (options.summaryProcess === 'calculate') {
                                const rate = options.value;
                                if (rate != null) {
                                    options.totalValue.sum += rate;
                                    options.totalValue.count += 1;
                                }
                            }

                            if (options.summaryProcess === 'finalize') {
                                const avg = options.totalValue.sum / options.totalValue.count;
                                options.totalValue = avg;
                            }
                        }
                    }}
                >
                    <TotalItem
                        column='loan'
                        summaryType='count'
                        showInColumn='loan'
                        displayFormat='Total / Avg {0}'
                    />
                    <TotalItem
                        column='units'
                        summaryType='sum'
                        showInColumn='units'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        name='estimatedSize'
                        column='estimatedSize'
                        summaryType='sum'
                        showInColumn='estimatedSize'
                        displayFormat='{0}'
                        valueFormat={'currency'}
                    />
                    <TotalItem
                        name='avgEstimatedRate'
                        column='estimatedRate'
                        summaryType='custom'
                        showInColumn='estimatedRate'
                        displayFormat='{0}'
                        customizeText={(e) => formatDecimalAsTwoDigitPercentage(e.value)}
                    />
                    <TotalItem
                        name='bfiRefi'
                        column='bfiRefi'
                        showInColumn='bfiRefi'
                        displayFormat='{0}'
                        summaryType='custom'
                    />
                </Summary>
            </StandardGroupingGrid>
        </>
    );
};
